/* FGD (Forge Game Data) lesen: Klassen, Eigenschaften, Auswahllisten, Spawnflags, Vererbung. Ohne DOM, node-testbar.
 * Format wie bei Hammer/Worldcraft: @PointClass base(A, B) size(..) color(..) = name : "Beschreibung" [ key(type) : "Label" : default = [ ... ] ] */

export type FgdKind = 'base' | 'point' | 'solid' | 'npc' | 'other';

export interface FgdChoice { value: string; label: string }
export interface FgdFlag { bit: number; label: string; on: boolean }
export interface FgdField {
  key: string;
  /** Typ klein geschrieben: string, integer, choices, flags, color255, target_source, ... */
  type: string;
  label: string;
  /** Standardwert (Text) oder undefined */
  dflt?: string;
  help?: string;
  readonly?: boolean;
  choices?: FgdChoice[];
  flags?: FgdFlag[];
}
/** Klassen-Eigenschaften name(args): rohe Argumente, nur bekannte werden ausgewertet */
export interface FgdClass {
  kind: FgdKind;
  name: string;
  desc: string;
  base: string[];
  /** Box in Einheiten (min, max); aus size(a b c, d e f) */
  size?: [number[], number[]];
  /** size(a b c) mit nur drei Zahlen: Kantenlängen, Mitte im Ursprung (Annahme, siehe docs/fgd-gaps.md) */
  sizeHalf?: boolean;
  color?: number[];
  /** studio("pfad.mdl"); leerer String = Modell steht im Feld model */
  studio?: string;
  iconsprite?: string;
  /** sprite() ohne Pfad: Sprite aus dem Feld model */
  sprite?: string;
  /** alle gelesenen Eigenschaften (Name klein) mit rohem Argumenttext */
  props: Record<string, string>;
  fields: FgdField[];
  line: number;
}
export interface FgdFile {
  classes: FgdClass[];
  includes: string[];
  warnings: string[];
}

type Tok = { t: 'w' | 's' | 'p'; v: string; line: number; bol: boolean };

const PUNCT = '@=:[](),+';

function lex(text: string, warn: (m: string) => void): Tok[] {
  const out: Tok[] = [];
  let i = 0, line = 1, bol = true;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    if (c === '\n') { line++; bol = true; i++; continue; }
    if (c === ' ' || c === '\t' || c === '\r' || c === '﻿') { i++; continue; }
    if (c === '/' && text[i + 1] === '/') { while (i < n && text[i] !== '\n') i++; continue; }
    if (c === '"') {
      const start = line; let j = i + 1, s = '';
      while (j < n && text[j] !== '"') { if (text[j] === '\n') line++; s += text[j]; j++; }
      if (j >= n) warn('Zeile ' + start + ': Zeichenkette nicht geschlossen');
      out.push({ t: 's', v: s, line: start, bol }); bol = false; i = j + 1; continue;
    }
    if (PUNCT.indexOf(c) >= 0) { out.push({ t: 'p', v: c, line, bol }); bol = false; i++; continue; }
    let j = i;
    while (j < n && !/[\s"()[\]=:,+@]/.test(text[j]) && !(text[j] === '/' && text[j + 1] === '/')) j++;
    if (j === i) { i++; continue; }
    out.push({ t: 'w', v: text.slice(i, j), line, bol }); bol = false; i = j;
  }
  return out;
}

const NUM = /^-?\d+(\.\d+)?$/;
const numsOf = (s: string): number[] => (s.match(/-?\d+(\.\d+)?/g) || []).map(Number);
const unq = (s: string): string => s.trim().replace(/^"|"$/g, '');

class Reader {
  p = 0;
  toks: Tok[];
  warn: (m: string) => void;
  constructor(toks: Tok[], warn: (m: string) => void) { this.toks = toks; this.warn = warn; }
  get cur(): Tok | undefined { return this.toks[this.p]; }
  isP(v: string): boolean { const c = this.cur; return !!c && c.t === 'p' && c.v === v; }
  line(): number { const c = this.cur || this.toks[this.toks.length - 1]; return c ? c.line : 0; }
  /** "a" + "b" ... zusammenfügen; erwartet einen String als Start */
  str(): string {
    let s = this.cur && this.cur.t === 's' ? this.toks[this.p++].v : '';
    while (this.isP('+') && this.toks[this.p + 1] && this.toks[this.p + 1].t === 's') { this.p += 2; s += this.toks[this.p - 1].v; }
    return s;
  }
  /** Argumenttext zwischen ( und ) roh einsammeln; Zeichenketten bleiben in Anführungszeichen */
  args(): string {
    this.p++;   // (
    let s = '', depth = 1;
    while (this.cur) {
      const c = this.cur;
      if (c.t === 'p' && c.v === '(') depth++;
      if (c.t === 'p' && c.v === ')' && --depth === 0) { this.p++; return s.trim(); }
      s += (c.t === 's' ? '"' + c.v + '"' : c.v) + ' ';
      this.p++;
    }
    return s.trim();
  }
  /** nach einem Fehler bis zur nächsten Zeile mit @ am Anfang springen */
  recover(): void { if (!(this.cur && this.cur.t === 'p' && this.cur.v === '@' && this.cur.bol)) this.p++; while (this.cur && !(this.cur.t === 'p' && this.cur.v === '@' && this.cur.bol)) this.p++; }
}

const KINDS: Record<string, FgdKind> = { baseclass: 'base', pointclass: 'point', solidclass: 'solid', npcclass: 'npc', keyframeclass: 'point', moveclass: 'point', filterclass: 'point' };

function readFields(r: Reader, cls: FgdClass): void {
  // r steht auf [
  r.p++;
  while (r.cur && !r.isP(']')) {
    const k = r.cur;
    if (k.t !== 'w') { r.warn('Zeile ' + k.line + ': unerwartetes Zeichen "' + k.v + '" in ' + cls.name); r.p++; continue; }
    const lowKey = k.v.toLowerCase();
    // Source-Eingaben und -Ausgaben: input Name(void) : "..."; hier ohne Bedeutung
    if ((lowKey === 'input' || lowKey === 'output') && r.toks[r.p + 1] && r.toks[r.p + 1].t === 'w') {
      r.p += 2; if (r.isP('(')) r.args();
      if (r.isP(':')) { r.p++; r.str(); }
      continue;
    }
    r.p++;
    if (!r.isP('(')) { r.warn('Zeile ' + k.line + ': Feld "' + k.v + '" ohne Typ in ' + cls.name); continue; }
    const type = r.args().toLowerCase();
    const f: FgdField = { key: k.v, type, label: '' };
    while (r.cur && r.cur.t === 'w' && /^(readonly|report)$/i.test(r.cur.v)) { if (/^readonly$/i.test(r.cur.v)) f.readonly = true; r.p++; }
    if (r.isP(':')) {
      r.p++; f.label = r.str();
      if (r.isP(':')) {
        r.p++;
        if (r.cur && r.cur.t === 's') f.dflt = r.str();
        else if (r.cur && r.cur.t === 'w' && NUM.test(r.cur.v)) f.dflt = r.toks[r.p++].v;
        if (r.isP(':')) { r.p++; f.help = r.str(); }
      }
    }
    if (r.isP('=')) {
      r.p++;
      if (!r.isP('[')) { r.warn('Zeile ' + r.line() + ': Liste für ' + f.key + ' fehlt'); continue; }
      r.p++;
      const choices: FgdChoice[] = [], flags: FgdFlag[] = [];
      while (r.cur && !r.isP(']')) {
        const v = r.cur;
        if (v.t === 'p') { r.p++; continue; }
        r.p++;
        let label = '', on = false;
        if (r.isP(':')) {
          r.p++; label = r.str();
          if (r.isP(':')) {
            r.p++; if (r.cur && r.cur.t === 'w' && NUM.test(r.cur.v)) on = r.toks[r.p++].v !== '0';
            if (r.isP(':')) { r.p++; r.str(); }   // Hilfetext
          }
        }
        if (type === 'flags') {
          const bit = Number(v.v);
          if (!NUM.test(v.v) || !(bit > 0)) r.warn('Zeile ' + v.line + ': Spawnflag "' + v.v + '" in ' + cls.name + ' ist keine Zahl');
          else flags.push({ bit, label, on });
        } else choices.push({ value: v.v, label });
      }
      if (r.isP(']')) r.p++;
      if (type === 'flags') f.flags = flags; else f.choices = choices;
    }
    cls.fields.push(f);
  }
  if (r.isP(']')) r.p++;
}

function readClass(r: Reader, kw: string, kind: FgdKind, out: FgdClass[]): void {
  const cls: FgdClass = { kind, name: '', desc: '', base: [], props: {}, fields: [], line: r.line() };
  // Eigenschaften name(args) bis zum =
  while (r.cur && r.cur.t === 'w' && r.toks[r.p + 1] && r.toks[r.p + 1].t === 'p' && r.toks[r.p + 1].v === '(') {
    const name = r.cur.v.toLowerCase(); r.p++;
    const args = r.args();
    cls.props[name] = args;
    if (name === 'base') cls.base = args.split(',').map((s) => s.trim()).filter(Boolean);
    else if (name === 'size') {
      const n = numsOf(args);
      if (n.length === 6) cls.size = [n.slice(0, 3), n.slice(3)];
      else if (n.length === 3) { cls.size = [n.map((x) => -x / 2), n.map((x) => x / 2)]; cls.sizeHalf = true; }
      else r.warn('Zeile ' + cls.line + ': size(' + args + ') hat keine 3 oder 6 Zahlen');
    } else if (name === 'color') {
      const n = numsOf(args); if (n.length >= 3) cls.color = n.slice(0, 3).map((x) => Math.max(0, Math.min(255, Math.round(x))));
    } else if (name === 'studio') cls.studio = unq(args);
    else if (name === 'iconsprite') cls.iconsprite = unq(args);
    else if (name === 'sprite') cls.sprite = unq(args);
  }
  if (!r.isP('=')) { r.warn('Zeile ' + cls.line + ': @' + kw + ' ohne "=" und Namen'); r.recover(); return; }
  r.p++;
  if (!r.cur || r.cur.t !== 'w') { r.warn('Zeile ' + cls.line + ': @' + kw + ' ohne Klassennamen'); r.recover(); return; }
  cls.name = r.toks[r.p++].v;
  if (r.isP(':')) { r.p++; cls.desc = r.str(); if (r.isP(':')) { r.p++; cls.props.help = r.str(); } }
  if (r.isP('[')) readFields(r, cls);
  else r.warn('Zeile ' + cls.line + ': ' + cls.name + ' ohne [ ]');
  out.push(cls);
}

/** Text einer FGD-Datei lesen. Fehler in einzelnen Klassen führen zu einer Warnung, nicht zum Abbruch. */
export function parseFgd(text: string): FgdFile {
  const warnings: string[] = [];
  const warn = (m: string): void => { warnings.push(m); };
  const r = new Reader(lex(text, warn), warn);
  const classes: FgdClass[] = [], includes: string[] = [];
  while (r.cur) {
    if (!(r.isP('@'))) { r.p++; continue; }
    const at = r.cur.line;
    r.p++;
    const kw = r.cur && r.cur.t === 'w' ? r.cur.v : '';
    const low = kw.toLowerCase();
    if (!kw) { warn('Zeile ' + at + ': @ ohne Schlüsselwort'); continue; }
    r.p++;
    try {
      if (KINDS[low]) readClass(r, kw, KINDS[low], classes);
      else if (low === 'include') { if (r.cur && r.cur.t === 's') includes.push(r.toks[r.p++].v); else warn('Zeile ' + at + ': @include ohne Dateiname'); }
      else if (low === 'mapsize') { if (r.isP('(')) r.args(); }
      else if (low === 'autovisgroup' || low === 'materialexclusion') {
        // = "Name" [ ... ] mit verschachtelten Listen überspringen
        let depth = 0;
        while (r.cur) {
          if (r.isP('[')) depth++;
          if (r.isP(']') && --depth <= 0) { r.p++; break; }
          r.p++;
        }
      } else { warn('Zeile ' + at + ': unbekanntes Schlüsselwort @' + kw); r.recover(); }
    } catch (e) { warn('Zeile ' + at + ': ' + (e as Error).message); r.recover(); }
  }
  return { classes, includes, warnings };
}

/** Klasse nach Auflösung der Vererbung */
export interface FgdResolved {
  name: string;
  kind: FgdKind;
  desc: string;
  size?: [number[], number[]];
  sizeHalf?: boolean;
  color?: number[];
  studio?: string;
  iconsprite?: string;
  sprite?: string;
  props: Record<string, string>;
  /** Felder ohne spawnflags */
  fields: FgdField[];
  /** Spawnflags aus allen Basisklassen */
  flags: FgdFlag[];
}

export interface FgdSet {
  /** nur konkrete Klassen (keine @BaseClass), nach Name */
  classes: Map<string, FgdResolved>;
  warnings: string[];
}

/** Zwei Definitionen derselben Klasse zusammenführen: additiv wie bei mehreren Game-Data-Dateien in Hammer. Felder und Spawnflags beider Seiten bleiben erhalten;
 *  bei gleichem Schlüssel bzw. gleichem Bit gewinnt die spätere Datei, ebenso bei Beschreibung, Größe, Farbe und Modell, sofern sie dort etwas setzt. */
export function mergeClasses(a: FgdClass, b: FgdClass): FgdClass {
  const fields: FgdField[] = a.fields.slice();
  for (const f of b.fields) {
    const i = fields.findIndex((x) => x.key.toLowerCase() === f.key.toLowerCase());
    if (i < 0) { fields.push(f); continue; }
    const old = fields[i];
    if (old.type === 'flags' && f.type === 'flags') {
      const flags = (old.flags || []).slice();
      for (const fl of f.flags || []) { const j = flags.findIndex((x) => x.bit === fl.bit); if (j < 0) flags.push(fl); else flags[j] = fl; }
      fields[i] = { ...f, flags };
    } else fields[i] = f;
  }
  const base = a.base.concat(b.base.filter((x) => !a.base.some((y) => y.toLowerCase() === x.toLowerCase())));
  return {
    ...a, ...b, base, fields, props: { ...a.props, ...b.props }, desc: b.desc || a.desc,
    size: b.size || a.size, sizeHalf: b.size ? b.sizeHalf : a.sizeHalf, color: b.color || a.color,
    studio: b.studio !== undefined ? b.studio : a.studio, iconsprite: b.iconsprite !== undefined ? b.iconsprite : a.iconsprite, sprite: b.sprite !== undefined ? b.sprite : a.sprite
  };
}

/** Dateien zusammenführen und Basisklassen auflösen. Gleichnamige Klassen aus mehreren Dateien werden additiv vereint (`mergeClasses`). */
export function resolveFgds(files: FgdFile[], names?: string[]): FgdSet {
  const warnings: string[] = [];
  if (names) {
    const have = new Set(names.map((n) => n.replace(/^.*[\\/]/, '').toLowerCase()));
    files.forEach((f, i) => { for (const inc of f.includes) if (!have.has(inc.replace(/^.*[\\/]/, '').toLowerCase())) warnings.push((names[i] || 'FGD') + ': @include "' + inc + '" ist nicht geladen'); });
  }
  // Basisklassen und Entities haben getrennte Namensräume (z. B. @BaseClass Light und @PointClass light)
  const bases = new Map<string, FgdClass>(), ents = new Map<string, FgdClass>();
  for (const f of files) {
    for (const w of f.warnings) warnings.push(w);
    for (const c of f.classes) {
      const m = c.kind === 'base' ? bases : ents, k = c.name.toLowerCase(), old = m.get(k);
      m.set(k, old ? mergeClasses(old, c) : c);
    }
  }
  const find = (n: string): FgdClass | undefined => bases.get(n.toLowerCase()) || ents.get(n.toLowerCase());
  const memo = new Map<string, FgdResolved>();
  const walk = (c: FgdClass, stack: string[]): FgdResolved => {
    const key = (c.kind === 'base' ? 'b:' : 'e:') + c.name.toLowerCase();
    const have = memo.get(key);
    if (have) return have;
    const res: FgdResolved = { name: c.name, kind: c.kind, desc: '', props: {}, fields: [], flags: [] };
    const fieldAt = new Map<string, number>(), flagAt = new Map<number, number>();
    const merge = (src: { size?: [number[], number[]]; sizeHalf?: boolean; color?: number[]; studio?: string; iconsprite?: string; sprite?: string; props: Record<string, string>; fields: FgdField[]; flags: FgdFlag[] }): void => {
      if (src.size) { res.size = src.size; res.sizeHalf = src.sizeHalf; }
      if (src.color) res.color = src.color;
      if (src.studio !== undefined) res.studio = src.studio;
      if (src.iconsprite !== undefined) res.iconsprite = src.iconsprite;
      if (src.sprite !== undefined) res.sprite = src.sprite;
      Object.assign(res.props, src.props);
      for (const f of src.fields) {
        const i = fieldAt.get(f.key.toLowerCase());
        if (i === undefined) { fieldAt.set(f.key.toLowerCase(), res.fields.length); res.fields.push(f); } else res.fields[i] = f;
      }
      for (const f of src.flags) {
        const i = flagAt.get(f.bit);
        if (i === undefined) { flagAt.set(f.bit, res.flags.length); res.flags.push(f); } else res.flags[i] = f;
      }
    };
    for (const bn of c.base) {
      const b = find(bn);
      if (!b) { warnings.push(c.name + ': Basisklasse "' + bn + '" nicht gefunden'); continue; }
      if (stack.indexOf((b.kind === 'base' ? 'b:' : 'e:') + b.name.toLowerCase()) >= 0) { warnings.push(c.name + ': Basisklassen verweisen im Kreis auf "' + bn + '"'); continue; }
      merge(walk(b, stack.concat(key)));
    }
    const own = c.fields.filter((f) => f.type !== 'flags');
    const ownFlags: FgdFlag[] = [];
    for (const f of c.fields) if (f.type === 'flags') for (const fl of f.flags || []) ownFlags.push(fl);
    merge({ size: c.size, sizeHalf: c.sizeHalf, color: c.color, studio: c.studio, iconsprite: c.iconsprite, sprite: c.sprite, props: c.props, fields: own, flags: ownFlags });
    res.desc = c.desc;
    memo.set(key, res);
    return res;
  };
  const classes = new Map<string, FgdResolved>();
  for (const c of ents.values()) classes.set(c.name, walk(c, []));
  return { classes, warnings };
}

/** Bequem: Texte mehrerer Dateien in einem Schritt */
export function loadFgds(texts: string[], names?: string[]): FgdSet {
  return resolveFgds(texts.map(parseFgd), names);
}
